// screens/marketplace/MarketplaceOverview.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tags,
  Package,
  CalendarDays,
  SlidersHorizontal,
  ReceiptText,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useToast } from "../../providers/ToastProvider";
import { PageShell, Section, Button, useTokens } from "./shared";
import { useCart } from "../../contexts/CartContext";


const CARD_DEFS = {
  categories: {
    admin: true,
    icon: Tags,
    title: "Categories",
    description: "Groups customers use to browse the catalogue.",
    path: "/marketplace/manage/categories",
  },
  products: {
    admin: true,
    icon: Package,
    title: "Product catalogue",
    description: "Create and manage products, stock, and base pricing.",
    path: "/marketplace/manage/products",
  },
  campaigns: {
    admin: true,
    icon: CalendarDays,
    title: "Preorder campaigns",
    description: "Open timed windows where orders are batched and shipped together.",
    path: "/marketplace/manage/campaigns",
  },
  pricing: {
    admin: false,
    icon: SlidersHorizontal,
    title: "Your store prices",
    description: "Set the prices customers see on your storefront.",
    path: "/marketplace/manage/pricing",
  },
  orders: {
    admin: true,
    icon: ReceiptText,
    title: "Marketplace orders",
    description: "Update fulfilment, add tracking, and handle refunds.",
    path: "/marketplace/manage/orders",
  },
  agentOrders: {
    admin: false,
    icon: ReceiptText,
    title: "Your attributed orders",
    description: "Read-only view of orders placed through your storefront.",
    path: "/marketplace/manage/orders",
  },
};

export default function MarketplaceOverview() {
  const { authFetch, user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const t = useTokens();

  const [counts, setCounts] = useState({
    products: 0,
    orders: 0,
    categories: 0,
    campaigns: 0,
    pricing: 0,
  });
  const [loading, setLoading] = useState(true);

  const admin = user?.role === "ADMIN";

  const load = useCallback(async () => {
    try {
      const urls = ["products/", "orders/", "pricing/"];
      if (admin) urls.push("categories/", "pre-orders/");
      const results = await Promise.all(
        urls.map((u) => authFetch("/marketplace/" + u))
      );
      const bodies = await Promise.all(results.map((r) => r.json()));
      setCounts({
        products: (bodies[0].data || []).length,
        orders: (bodies[1].data || []).length,
        pricing: (bodies[2].data || []).length,
        categories: admin ? (bodies[3].data || []).length : 0,
        campaigns: admin ? (bodies[4].data || []).length : 0,
      });
    } catch {
      showToast("Could not load marketplace summary", "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch, admin, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const keys = admin
    ? ["categories", "products", "campaigns", "orders"]
    : ["products", "pricing", "agentOrders"];

  const counterFor = (key) => {
    if (key === "categories") return counts.categories;
    if (key === "products") return counts.products;
    if (key === "campaigns") return counts.campaigns;
    if (key === "pricing") return counts.pricing;
    return counts.orders;
  };

  return (
    <PageShell
      eyebrow={admin ? "Operations centre" : "Your marketplace"}
      title="Marketplace management"
      subtitle={
        admin
          ? "Manage catalogue, preorder windows, pricing rules, and fulfilment from one place."
          : "Set validated prices for your storefront and review attributed orders."
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 14,
        }}
      >
        {keys.map((key) => {
          const def = CARD_DEFS[key];
          const Icon = def.icon;
          return (
            <button
              key={key}
              onClick={() => navigate(def.path)}
              style={{
                textAlign: "left",
                background: t.surface,
                border: `1px solid ${t.border}`,
                borderRadius: 14,
                padding: 20,
                cursor: "pointer",
                transition:
                  "transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
                display: "flex",
                flexDirection: "column",
                gap: 16,
                fontFamily: "inherit",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.borderColor = t.accent;
                e.currentTarget.style.boxShadow = `0 12px 32px ${t.shadow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = t.border;
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: t.subtle,
                    color: t.accent,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon size={20} />
                </span>
                <ArrowRight size={16} color={t.muted} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: t.heading,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {def.title}
                </div>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: 13,
                    color: t.muted,
                    lineHeight: 1.5,
                  }}
                >
                  {def.description}
                </p>
              </div>
              <div
                style={{
                  marginTop: "auto",
                  fontSize: 12,
                  fontWeight: 700,
                  color: t.accent,
                }}
              >
                {loading ? "—" : `${counterFor(key)} item${counterFor(key) === 1 ? "" : "s"}`}
              </div>
            </button>
          );
        })}
      </div>
    </PageShell>
  );
}